import { useEffect, useState, useMemo } from "react";
import type { RootState } from "../store/store";
import { useSelector } from "react-redux";
import Station from "../components/stations/station-detail";
import ormStations from "../hooks/app/stations";
import ORMfunctions from "../hooks/app/general";

const getPosition = (): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject);
  });
};

function StationsList(props: any) {
  const reduxStations = useSelector(
    (state: RootState) => state.stations.stations,
  );
  const geolocation = props.geolocation;

  const [displayStations, setDisplayStations] = useState<any[]>([]);
  const [allStations, setAllStations] = useState<any[]>([]);
  const [selectedLetter, setSelectedLetter] = useState<string>("all");
  const [filterOpen, setFilterOpen] = useState<boolean>(false);

  const availableLetters = useMemo(() => {
    const letters = new Set<string>();
    allStations.forEach((station: any) => {
      const firstLetter = station.name?.charAt(0).toUpperCase();
      if (firstLetter && /[A-Z]/.test(firstLetter)) {
        letters.add(firstLetter);
      }
    });
    return Array.from(letters).sort();
  }, [allStations]);

  useEffect(() => {
    if (props.stationid) {
      const selecedStation = reduxStations.find(
        (element: any) => element.id === props.stationid,
      );
      if (selecedStation) {
        setAllStations([selecedStation]);
        setDisplayStations([selecedStation]);
      }
    } else {
      setAllStations(reduxStations);
      setDisplayStations(reduxStations);
    }
  }, [reduxStations]);

  useEffect(() => {
    const updateStationsWithLocation = async () => {
      try {
        if (navigator.geolocation && geolocation) {
          const position = await getPosition();

          const sorted = await Promise.all(
            reduxStations.map((s: any) => {
              return ormStations.stationDistance(s, {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });
            }),
          );

          sorted.sort((a, b) => (a.distance || 0) - (b.distance || 0));

          if (props.stationid) {
            const selecedStation = sorted.find(
              (element: any) => element.id === props.stationid,
            );
            if (selecedStation) {
              setAllStations([selecedStation]);
              setDisplayStations([selecedStation]);
            }
          } else {
            setAllStations(sorted);

            if (selectedLetter === "all") {
              setDisplayStations(sorted);
            }
          }
        }
      } catch (error) {
        // console.error("Geolocation Error:", error);
      }
    };

    if (reduxStations.length > 0) {
      updateStationsWithLocation();
    }
  }, [reduxStations, geolocation]);

  useEffect(() => {
    if (selectedLetter === "all") {
      setDisplayStations(allStations);
    } else {
      const filtered = allStations.filter((station: any) =>
        station.name?.toUpperCase().startsWith(selectedLetter),
      );
      setDisplayStations(filtered);
    }
  }, [selectedLetter, allStations]);

  const handleLetterClick = (letter: string) => {
    setSelectedLetter(letter);
    setFilterOpen(false);
  };

  const handleKeyDown = (e: any) => {
    const isNavKey = [
      "ArrowUp",
      "ArrowDown",
      "Up",
      "Down",
      "Home",
      "End",
    ].includes(e.key);

    // logic for closed dropdown, applies to button
    if (!filterOpen) {
      if (isNavKey) {
        e.preventDefault();
        setFilterOpen(true);

        if (["ArrowUp", "Up", "Home"].includes(e.key)) {
        } else if (["End"].includes(e.key)) {
        }
      }
      return;
    }
    switch (e.key) {
      case "ArrowUp":
      case "Up":
        e.preventDefault();

        break;
      case "ArrowDown":
      case "Down":
        e.preventDefault();

        break;
      case "Home":
      case "PageUp":
        e.preventDefault();

        break;
      case "End":
      case "PageDown":
        e.preventDefault();

        break;
      case "Enter":
      case " ":
        // e.preventDefault();
        break;
      case "Tab":
        break;
      case "Esc":
      case "Escape":
        e.preventDefault();
        setFilterOpen(false);
        break;
      default:
        break;
    }
  };

  if (props.stationid) {
    return (
      <div>
        {displayStations.length > 0 ? (
          displayStations.map((station: any) => (
            <Station
              station={station}
              key={station.id}
              type={props.type}
              detailpage={props.detailpage}
              showdetails={props.showdetails}
            />
          ))
        ) : (
          <p>Keine Stationen mit "{selectedLetter}" gefunden</p>
        )}
      </div>
    );
  } else {
    return (
      <div>
        {props.showfilter && (
          <div className="orm-stationfilter">
            <button
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={filterOpen}
              aria-controls="listbox"
              aria-label={ORMfunctions.i18n("general/filter") || "Filter"}
              className="orm-stationfilter-button"
              onClick={() => setFilterOpen(!filterOpen)}
              onKeyDown={handleKeyDown}
            >
              Filter
              <span className="icon">
                <i
                  className={
                    filterOpen ? "fas fa-chevron-up" : "fas fa-chevron-down"
                  }
                ></i>
              </span>
            </button>
            <span className="orm-stationfilter-label">
              {ORMfunctions.i18n("general/filter") || "Filter"}
            </span>
            <div
              role="listbox"
              aria-label="Filter"
              className={
                filterOpen
                  ? "orm-stationfilter-list open"
                  : "orm-stationfilter-list"
              }
            >
              <button
                onClick={() => handleLetterClick("all")}
                onKeyDown={handleKeyDown}
                role="option"
                aria-selected={selectedLetter === "all"}
                className={
                  selectedLetter === "all"
                    ? "active orm-stationfilter-btn orm-stationfilter-btn--all"
                    : "orm-stationfilter-btn orm-stationfilter-btn--all"
                }
              >
                {ORMfunctions.i18n("general/all") || "Alle"}
              </button>
              {availableLetters.map((letter) => (
                <button
                  key={letter}
                  onClick={() => handleLetterClick(letter)}
                  onKeyDown={handleKeyDown}
                  role="option"
                  aria-selected={selectedLetter === letter}
                  className={
                    selectedLetter === letter
                      ? "active orm-stationfilter-btn"
                      : "orm-stationfilter-btn"
                  }
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>
        )}
        {props.type == "standard" ? (
          <div className="orm-stationsfilter-station-list orm-row">
            {displayStations.length > 0 ? (
              displayStations.map((station: any) => (
                <div
                  className={`orm-col-xs-12 orm-col-lg-${props.showdetails === "location" ? "3" : "6"} orm-mb-48`}
                  key={station.id}
                >
                  <Station
                    station={station}
                    type={props.type}
                    detailpage={props.detailpage}
                    showdetails={props.showdetails}
                  />
                </div>
              ))
            ) : (
              <></>
            )}
          </div>
        ) : (
          <ul className="orm-row orm-nonenstyledlist">
            {displayStations.length > 0 ? (
              displayStations.map((station: any) => (
                <Station
                  station={station}
                  key={station.id}
                  type={props.type}
                  detailpage={props.detailpage}
                  showdetails={props.showdetails}
                />
              ))
            ) : (
              <></>
            )}
          </ul>
        )}
      </div>
    );
  }
}

export default StationsList;
